/* eslint-disable no-param-reassign */
import { getContenTypePorTipoDePadron, getTipoDePadronPorContenType } from 'api/catalogos';
import Moment from 'moment';
import addressToString from 'utils/address';
import API from 'utils/api';
import { ESTADOS_GLOBALES_MAP } from 'utils/estadosGlobales';
import formatValues, { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import { gettersPadronesCajaMap } from 'utils/padrones';
import { MAPPERS, normalizeObject } from 'views/PAE/utils/mapping';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import { postEstadoDeEjecucion } from './estadosDeEjecucion';
import { getZonaDeDiligencia } from './zonasDeDiligencia';

export const baseURI = 'recaudacion/ejecuciones-pae/';

/*
  EJECUCIONES OBJECT
  {
    "id": number,
    "descripcion": string,
    "origen_del_credito": number, Relacion con la tabla de origenes de credito
    "actuacion": number, Relacion con la tabla de actuaciones
    "adjunta_documentos": boolean,
    "reportes_id_a_imprimir": number
    "fundamento_legal": string | null,
    "tiene_vencimiento": boolean,
    "orden_de_uso": number,
    "vence_en_dias_habiles": boolean,
    "imprime_reportes": boolean,
    "envia_correo_electronico": boolean,
    "envia_mensaje_eletronico_sms": boolean,
    "titulo_de_correo": string | null,
    "contenido_de_correo": string | null,
    "contenido_de_mensaje": string | null,
    "cobros_relacionados_a_ejecutor": boolean,
    "agrega_cobros_a_cartera": boolean,
    "cancelar_cobros_activos_al_revertir": boolean,
    "asigna_ejecutor_iniciando_estado": boolean,
    "asigna_ejecutor_finalizando_estado": boolean,
    "observaciones": string | null,
    "numero_de_dias_de_vencimiento": number,
    "estados_globales": number Relacion con la tabla de estados globales

    TODO:  AGREGAR ESTOS DATOS
    usar_datos_del_padron: boolean
    usar_datos_del_contribuyente: boolean

    // solo lectura
    existen_adeudos_ejecutados_insolutos: boolean
    existen_adeudos_originados_en_ejecucion_insolutos: boolean

    // PENDIENTE
    lugar_de_nacimiento_constitucion_de_contribuyente
  }
*/

/**
 * GET Ejecuciones
 * param {object} params
 * @returns {Promise<object>}
 */
export const getEjecuciones = async (params) => {
  try {
    const response = await API.get(baseURI, { params });

    if (response?.data?.results) {
      const results = formatReceived(response.data.results).map((ejecucion) => ({
        ...ejecucion,
        tipo_de_padron: getTipoDePadronPorContenType(ejecucion.content_type_padron),
      }));

      return {
        ...response.data,
        results,
      };
    }
    return response.data.map((ejecucion) => ({
      ...ejecucion,
      tipo_de_padron: getTipoDePadronPorContenType(ejecucion.content_type_padron),
    })).sort((a, b) => a.orden_de_uso - b.orden_de_uso);
  } catch (err) {
    onError(err);
    return params?.page ? {
      results: [],
      count: 0,
      next: null,
      previous: null,
    } : [];
  }
};

const updateDatosDeEjecucion = async (id, values) => {
  try {
    const response = await API.patch(`${baseURI}${id}`, {
      datos_de_ejecucion_json: JSON.stringify(values),
      ultimo_estado_de_ejecucion: values?.ultimo_estado_de_ejecucion,
    });
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * GET Ejecución
 * @param {number} id
 * @returns {Promise<object>}
 */
export const getEjecucion = async (id) => {
  try {
    const response = await API.get(`${baseURI}${id}`);
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return null;
  }
};

const extractAdressData = (padron, key = 'direccion') => {
  const direccionObject = padron[key];
  if (!direccionObject || !direccionObject?.id) return { id: null, direccion: null };

  const direccionString = addressToString(direccionObject)?.toUpperCase();
  return {
    id: direccionObject.id,
    direccion: direccionString,
  };
};

const crearLogInicialDeEjecucion = async (values, id) => {
  try {
    let zona = '';

    if (values.ejecucion.zona_de_diligencia) {
      const _zona = await getZonaDeDiligencia(values.ejecucion.zona_de_diligencia);
      zona = _zona.descripcion;
    }

    const {
      id: estatus_de_ejecucion,
      actuacion,
      imprime_reportes,
      reportes_id_a_imprimir,
      adjunta_documentos,
      solo_uso_interno,
    } = values.estatusDeEjecucion;

    const response = await postEstadoDeEjecucion({
      estatus_de_ejecucion,
      estatus_previo_de_ejecucion: null,
      actuacion,
      actuacion_previa: null,
      ejecucion: id,
      estado_previo_de_ejecucion: null,
      fecha_de_actuacion: new Moment(),
      zona_de_diligencia: zona,
      imprime_reportes,
      reportes_id_impresos: reportes_id_a_imprimir,
      tiene_gastos_de_ejecucion: false,
      tiene_cobros_relacionados_a_ejecutor: false,
      adjunto_documentos: adjunta_documentos,
      es_unico_inicial: true,
      es_unico_de_cancelacion_de_ejecucion: false,
      es_final: false,
      es_pago: false,
      es_cancelacion_de_pago: false,
      solo_uso_interno,
      impedir_cambiar_cartera_ejecutada: false,
      es_recibo_cancelado: false,
    });
    return response;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * POST Ejecución
  * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const postEjecucion = async (values) => {
  try {
    const {
      direccion: direccion_del_padron,
      id: direccion_del_padron_id,
    } = extractAdressData(values.padron);

    const {
      direccion: direccion_de_notificacion_del_padron,
      id: direccion_de_notificacion_del_padron_id,
    } = extractAdressData(values.padron, 'direccion_notificacion_index');

    const {
      direccion: direccion_de_notificacion_del_contribuyente,
      id: direccion_de_notificacion_del_contribuyente_id,
    } = extractAdressData(values.contribuyente, 'direcciones_de_notificacion');

    const {
      id: direccion_del_contribuyente_id,
      direccion: direccion_del_contribuyente,
    } = extractAdressData(values.contribuyente);

    const normalizedContribuyente = normalizeObject(
      values.contribuyente,
      MAPPERS[padronesEnum.CONTRIBUYENTE],
    );

    const normalizedPadron = normalizeObject(
      values.padron,
      MAPPERS[values.tipo_de_padron],
    );

    let direccion_de_diligencia_data = null;
    if (values.ejecucion?.usar_datos_del_padron) {
      const getter = gettersPadronesCajaMap[values?.tipo_de_padron];
      const [padron] = await getter({ id: values.padron.id });
      direccion_de_diligencia_data = padron?.direccion_index?.id
        || padron?.direcciones?.id
        || padron?.direccion_notificacion_index?.id
        || padron?.direcciones_de_notificacion_index?.id;

      const directionObject = padron?.direccion_index
        || padron?.direcciones
        || padron?.direccion_notificacion_index
        || padron?.direcciones_de_notificacion_index;

      direccion_de_diligencia_data = {
        codigo_postal: directionObject?.codigo_postal?.id,
        estado: directionObject?.codigo_postal?.municipio?.estado?.nombre_de_AGEE,
        municipio: directionObject?.codigo_postal?.municipio?.nom_mun,
        tipo_de_vialidad: directionObject?.tipo_de_vialidad,
        calle_principal: directionObject?.calle_principal,
        numero_exterior: directionObject?.numero_exterior,
        numero_interior: directionObject?.numero_interior,
        tipo_de_vialidad_entre: directionObject?.tipo_de_vialidad_entre || ' ',
        entre_calle: directionObject?.entre_calle,
        tipo_de_vialidad_y_calle: directionObject?.tipo_de_vialidad_y_calle,
        y_calle: directionObject?.y_calle,
        descripcion_de_la_ubicacion: directionObject?.descripcion_de_la_ubicacion,
        cp_texto: directionObject?.cp_texto,
        colonia_texto: directionObject?.colonia_texto,
        municipio_texto: directionObject?.municipio_texto,
        estado_texto: directionObject?.estado_texto,
        longitud: directionObject?.longitud,
        latitud: directionObject?.latitud,
      };
    }

    if (values.ejecucion?.usar_datos_del_contribuyente) {
      direccion_de_diligencia_data = {
        codigo_postal: values.contribuyente?.direcciones?.codigo_postal?.id,
        estado: values.contribuyente?.direcciones?.codigo_postal?.municipio?.estado?.nombre_de_AGEE,
        municipio: values.contribuyente?.direcciones?.codigo_postal?.municipio?.nom_mun,
        tipo_de_vialidad: values.contribuyente?.direcciones?.tipo_de_vialidad,
        calle_principal: values.contribuyente?.direcciones?.calle_principal,
        numero_exterior: values.contribuyente?.direcciones?.numero_exterior,
        numero_interior: values.contribuyente?.direcciones?.numero_interior,
        tipo_de_vialidad_entre: values.contribuyente?.direcciones?.tipo_de_vialidad_entre,
        entre_calle: values.contribuyente?.direcciones?.entre_calle,
        tipo_de_vialidad_y_calle: values.contribuyente?.direcciones?.tipo_de_vialidad_y_calle,
        y_calle: values.contribuyente?.direcciones?.y_calle,
        descripcion_de_la_ubicacion: values.contribuyente?.direcciones?.descripcion_de_la_ubicacion,
        cp_texto: values.contribuyente?.direcciones?.cp_texto,
        colonia_texto: values.contribuyente?.direcciones?.colonia_texto,
        municipio_texto: values.contribuyente?.direcciones?.municipio_texto,
        estado_texto: values.contribuyente?.direcciones?.estado_texto,
        longitud: values.contribuyente?.direcciones?.longitud,
        latitud: values.contribuyente?.direcciones?.latitud,
      };
    }

    const formatedValues = formatValues({
      ...values.ejecucion,
      ...normalizedContribuyente,
      ...normalizedPadron,
      direccion_de_notificacion_del_padron,
      direccion_de_notificacion_del_padron_id,
      direccion_de_notificacion_del_contribuyente,
      direccion_de_notificacion_del_contribuyente_id,
      direccion_del_contribuyente,
      direccion_del_contribuyente_id,
      direccion_del_padron,
      direccion_del_padron_id,
      estado_previo_de_ejecucion: null,
      direccion_de_diligencia_data,
      object_id_padron: values.padron?.id,
      tipo_de_persona: values.contribuyente.tipo_de_persona,
      datos_de_json_del_padron: values.padron,
      datos_del_contribuyente_json: values.contribuyente,
    }, {
      clean: false,
    });

    formatedValues.content_type_padron = getContenTypePorTipoDePadron(values.tipo_de_padron);
    formatedValues.folio = '1';
    formatedValues.fecha_de_obligacion_inicial_de_adeudos = values?.ejecucion
      ?.fecha_de_obligacion_inicial_de_adeudos
      ? new Date(values?.ejecucion?.fecha_de_obligacion_inicial_de_adeudos).toISOString()
      : null;

    const response = await API.post(baseURI, formatedValues);
    const log = await crearLogInicialDeEjecucion(values, response.data.id);
    await updateDatosDeEjecucion(response.data.id, {
      ...values,
      ultimo_estado_de_ejecucion: log.id,
    });
    onSuccess(response, 'Agregado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

export const patchSingleEjecucion = async (id, values) => {
  try {
    const response = await API.patch(`${baseURI}${id}`, values);
    onSuccess(response, 'Actualizado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Ejecución
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const patchEjecucion = async (id, values) => {
  try {
    const formatedValues = formatValues({
      observaciones_del_padron: values?.ejecucion?.observaciones_del_padron,
      observaciones_del_contribuyente: values?.ejecucion?.observaciones_del_contribuyente,
      observaciones_del_adeudo: values?.ejecucion?.observaciones_del_adeudo,
      observaciones: values?.ejecucion?.observaciones,
      usar_datos_del_padron: values?.ejecucion?.usar_datos_del_padron ?? false,
      usar_datos_del_contribuyente: values?.ejecucion?.usar_datos_del_contribuyente ?? false,
      zona_de_diligencia: values?.ejecucion?.zona_de_diligencia,
      lada_de_telefono: values?.ejecucion?.lada_de_telefono,
      telefono_de_diligencia: values?.ejecucion?.telefono_de_diligencia,
      lada_celular_de_diligencia: values?.ejecucion?.lada_celular_de_diligencia,
      celular_de_diligencia: values?.ejecucion?.celular_de_diligencia,
      correo_electronico_de_diligencia: values?.ejecucion?.correo_electronico_de_diligencia,
    }, {
      clean: false,
    });

    const response = await API.patch(`${baseURI}${id}`, formatedValues);
    delete values?.ejecucion?.ultimo_estado_de_ejecucion;
    await updateDatosDeEjecucion(id, values?.ejecucion);
    onSuccess(response, 'Editado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * DELETE Ejecución
 * @param {number} id
 * @returns {Promise<object|boolean>}
 */
export const deleteEjecucion = async (id) => {
  try {
    const response = await API.patch(`${baseURI}${id}`, {
      estados_globales: ESTADOS_GLOBALES_MAP.CANCELADO,
    });
    onSuccess(response, 'Cancelado correctamente');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

export const triggerGeneracionMasiva = async (generacion_id) => {
  try {
    const response = await API.post('/recaudacion/procesamiento-cargos-pae/procesar-cargos/', {
      generacion_id,
    });
    return response.data;
  } catch (err) {
    onError(err);
    return {
      success: false,
      message: err?.response?.data?.message
      || 'Ocurrió un error al generar las ejecuciones. Por favor, intente nuevamente.',
    };
  }
};

/**
 * POST | Consulta de ejecuciones por criterios de búsqueda.
 *
 * Acepta distintos parámetros (todos opcionales, pero se requiere al menos uno)
 * para filtrar ejecuciones. Los parámetros pueden provenir del formulario simple
 * o del modal de búsqueda avanzada.
 *
 * Parámetros soportados (alias aceptados entre paréntesis):
 * - Datos simples
 *   - @param {Object} params
 *   - @param {string}  [params.folio_de_ejecucion] Folio textual de la ejecución.
 *   - @param {number}  [params.origen_del_credito] ID del origen del crédito.
 *   - @param {number}  [params.ultimo_estatus_de_ejecucion] ID del estatus actual de ejecución.
 *   - @param {number}  [params.siguiente_estatus] ID del siguiente estatus permitido.
 *   - @param {number|string} [params.tipo_de_padron] Identificador del tipo de padrón
 *            (puede ser el ID del padrón o su content_type según selección).
 *   - @param {number}  [params.object_id_padron]
 *            ID del objeto de padrón (padron_id seleccionado).
 *   - @param {number|string} [params.solo_cartera_ejecutada]
 *            Alias: (solo_con_estado_de_cartera_ejecutada_id). ID/clave del estado de
 *            cartera ejecutada por el cual filtrar.
 *   - @param {number|string} [params.solo_gastos_ejecucion]
 *            Alias: (solo_con_estado_de_gastos_de_ejecucion_id). ID/clave del estado de
 *            gastos de ejecución por el cual filtrar.
 *   - @param {string}  [params.q] Texto de búsqueda para localizar el padrón.
 *
 * - Búsqueda avanzada por rangos y folios
 *   - @param {string|number} [params.folio_inicial_de_ejecucion]
 *            Folio inicial (texto) de ejecución.
 *   - @param {string|number} [params.folio_final_de_ejecucion] Folio final (texto) de ejecución.
 *   - @param {number}  [params.folio_id_inicial_de_ejecucion] Folio ID inicial de ejecución.
 *   - @param {number}  [params.folio_id_final_de_ejecucion] Folio ID final de ejecución.
 *   - @param {number}  [params.folio_id_inicial_de_generacion] Folio ID inicial de generación.
 *   - @param {number}  [params.folio_id_final_de_generacion] Folio ID final de generación.
 *   - @param {[string,string]} [params.rango_de_fecha_de_ejecucion]
 *            Rango de fechas de ejecución en formato 'YYYY-MM-DD'. Alternativamente,
 *            el backend también puede aceptar:
 *            - @param {string} [params.fecha_inicial_de_ejecucion]
 *            - @param {string} [params.fecha_final_de_ejecucion]
 *
 * Retorno:
 * - @returns {Promise<object[]>} Lista de ejecuciones normalizadas mediante `formatReceived`.
 *
 * Ejemplos:
 * - Búsqueda simple por folio
 *   @example
 *   await postEjecucionesBySearch({ folio_de_ejecucion: 'ABC-123' });
 *
 * - Búsqueda avanzada por rango de fechas y estatus
 *   @example
 *   await postEjecucionesBySearch({
 *     origen_del_credito: 2,
 *     ultimo_estatus_de_ejecucion: 5,
 *   });
 */
export const postEjecucionesBySearch = async (params) => {
  try {
    const response = await API.post('/recaudacion/consulta-de-ejecuciones/', params);
    return formatReceived(response.data?.data);
  } catch (err) {
    onError(err);
    return [];
  }
};
